import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useLanguage } from './LanguageContext';

// We declare this to satisfy TypeScript, but we also check access at runtime
declare const __APP_VERSION__: number;

// Manually define ImportMeta to resolve 'env' property access error
// This replaces the need for "/// <reference types="vite/client" />" which was not found
declare global {
  interface ImportMeta {
    env: {
      DEV: boolean;
      [key: string]: any;
    };
  }
}

const VersionChecker: React.FC = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const { isRtl } = useLanguage();

  useEffect(() => {
    // Skip checking in development mode
    // Use optional chaining (env?.DEV) to avoid crashing if env is undefined
    if (import.meta.env?.DEV) return;

    const checkVersion = async () => {
      try {
        // Fetch version.json with a timestamp query param to bypass browser cache
        const res = await fetch(`/version.json?t=${Date.now()}`, {
          cache: 'no-store',
        });
        
        if (!res.ok) return;
        
        const data = await res.json();
        const serverVersion = data.version;
        
        // Safely access global variable injected by Vite
        let localVersion: number;
        try {
          localVersion = __APP_VERSION__;
        } catch (e) {
          // Fallback if the variable wasn't replaced/defined (ReferenceError)
          localVersion = 0; 
        }

        // If server version is newer than local build time
        if (serverVersion && localVersion && serverVersion > localVersion) {
          setUpdateAvailable(true);
        }
      } catch (error) {
        console.error('Error checking version:', error);
      }
    };

    // Check immediately on mount
    checkVersion();

    // Check again when the page becomes visible (e.g. user returns to tab)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  if (!updateAvailable) return null;

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className={`fixed bottom-4 left-4 right-4 md:left-auto md:right-4 z-[100] bg-[#1a120b]/95 border border-fait-gold/40 shadow-[0_0_20px_rgba(0,0,0,0.5)] rounded-xl p-4 backdrop-blur-md animate-slide-up flex items-center justify-between gap-6 max-w-md ${isRtl ? 'flex-row-reverse' : 'flex-row'}`}>
      <div className={`flex flex-col ${isRtl ? 'text-right' : 'text-left'}`}>
        <span className="text-white font-bold font-serif text-lg">
          {isRtl ? 'גרסה חדשה זמינה!' : 'Update Available!'}
        </span>
        <span className="text-gray-400 text-xs font-sans">
          {isRtl ? 'נוספו עדכונים לאתר. לחץ לרענון.' : 'New content available. Click to refresh.'}
        </span>
      </div>
      
      <button 
        onClick={handleRefresh}
        className="bg-fait-gold text-fait-dark px-4 py-2 rounded-lg font-bold hover:bg-white transition-colors flex items-center gap-2 shadow-lg group"
      >
        <RefreshCw size={16} className="group-hover:animate-spin" />
        {isRtl ? 'רענן' : 'Refresh'}
      </button>
    </div>
  );
};

export default VersionChecker;